<template>
  <div class="measure-row">
    <span ref="measureHeader" class="header"></span>
    <span ref="measureValue" class="value"></span>
    <span
      ref="measureGap"
      class="value"
      style="width: 2vh; display: block"
    ></span>
  </div>
  <div
    id="benchmark-container"
    :style="{ height: tableHeight }"
    ref="benchmarkContainer"
  >
    <transition-group name="metric-row">
      <div
        class="single-axis-benchmark"
        :class="{
          selected: String(playerResult)?.trim() == data.label?.trim(),
        }"
        v-for="(data, index) in displayedBenchmarks"
        :key="index"
      >
        <div
          class="header"
          :ref="'header' + data.label"
          :style="{ width: headerWidth }"
        >
          {{ data.label?.trim() }}
        </div>
        <div
          :style="{ width: getBarWidth(metricData.benchmarks[data.label]) }"
          class="bar"
        ></div>
        <div
          class="value"
          :ref="'value' + data.label"
          :style="{ width: valueWidth }"
        >
          {{ data["value"] || 0 }}%
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  props: {
    playerResult: {},
    metricData: {
      type: Object,
      required: true,
    },
    metric: {
      type: String,
      required: true,
    },
    maxBenchmarkLabels: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      gapWidth: "0.5vh",
      valueWidth: "auto",
      headerWidth: "auto",
      displayedBenchmarks: {},
      maxPercentageKey: "",
      containerWidth: "auto",
    };
  },
  computed: {
    tableHeight() {
      const totalRows = this.maxBenchmarkLabels;
      const rowHeight = 3.4;

      return `${totalRows * rowHeight + (totalRows - 1)}vh`;
    },
    maxMetricValue() {
      return Math.max(
        Object.values(this.metricData.benchmarks).reduce((maxValue, value) => {
          if (maxValue < value) {
            maxValue = value;
          }
          return maxValue;
        }, 0)
      );
    },
  },
  watch: {
    metric() {
      this.updateBarChart();
    },
  },
  methods: {
    animateBenchmark(label, target, index, duration = 500) {
      // check if index exists
      const start = this.displayedBenchmarks[index].value ?? target;
      this.displayedBenchmarks[index].label = label;
      const startTime = performance.now();

      const step = (time) => {
        const t = Math.min((time - startTime) / duration, 1);
        const eased = t ** 2 * (3 - 2 * t);

        this.displayedBenchmarks[index].value =
          Math.round((start + (target - start) * eased) * 10) / 10;
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          this.displayedBenchmarks[index].value = target;
        }
      };
      requestAnimationFrame(step);
    },
    async updateBarChart() {
      await this.indexCleanup();
      Object.keys(this.metricData.benchmarks)
        .sort()
        .forEach((label, index) => {
          this.animateBenchmark(
            label,
            this.metricData.benchmarks[label],
            index
          );
        });
      this.setLabelWidths();
    },
    async indexCleanup() {
      const newLength = Object.keys(this.metricData.benchmarks).length;
      if (newLength < this.displayedBenchmarks.length) {
        this.displayedBenchmarks = this.displayedBenchmarks.slice(0, newLength);
      } else {
        this.displayedBenchmarks.push(
          ...Array.from(
            { length: newLength - this.displayedBenchmarks.length },
            () => ({})
          )
        );
      }
    },
    getMaxRefWidth(prefix) {
      const widths = Object.entries(this.$refs)
        .filter(([key]) => key.startsWith(prefix))
        .map(([key]) => this.$refs[key]?.offsetWidth || 0);
      return widths.length ? Math.max(...widths) + "px" : "auto";
    },
    getLongestRefText(arr) {
      return arr.reduce(
        (longest, key) =>
          key.trim().length > longest.trim().length ? key : longest,
        ""
      );
    },
    async getWidths() {
      this.containerWidth =
        this.$refs.benchmarkContainer.getBoundingClientRect().width;

      this.$refs.measureHeader.textContent = await Object.keys(
        this.metricData.benchmarks
      ).reduce(
        (longest, key) =>
          key.trim().length > longest.trim().length ? key : longest,
        ""
      );
      this.headerWidth = this.$refs.measureHeader.offsetWidth;

      this.maxPercentageKey = Object.entries(this.metricData.benchmarks).reduce(
        (max, [key, value]) => (value > max.value ? { key, value } : max),
        { key: null, value: -Infinity }
      ).key;
      this.gapWidth = this.$refs.measureGap.getBoundingClientRect().width;
    },
    async setLabelWidths() {
      await this.getWidths();
      let valueWidths = Object.fromEntries(
        Object.keys(this.metricData.benchmarks).map((label) => {
          const value = this.metricData.benchmarks[label];
          this.$refs.measureValue.textContent = `${value}%`;
          const width = this.$refs.measureValue.getBoundingClientRect().width;
          return [label, { value, width }];
        })
      );
      let maxBarWidth =
        this.containerWidth -
        this.gapWidth -
        valueWidths[this.maxPercentageKey].width -
        this.headerWidth;

      this.valueWidth =
        valueWidths[
          Object.entries(valueWidths).reduce(
            (min, [key, data]) => {
              const calculation =
                this.containerWidth -
                maxBarWidth * (data.value / this.maxMetricValue) -
                this.gapWidth -
                this.headerWidth -
                data.width;
              return calculation < min.result
                ? { key, result: calculation }
                : min;
            },
            { key: null, result: Infinity }
          ).key
        ].width;
      this.valueWidth += "px";
      this.headerWidth += "px";
    },
    getBarWidth(value) {
      try {
        const decimal = parseFloat(value) / this.maxMetricValue;
        return `calc((100% - ${this.headerWidth} - 1vh - ${this.valueWidth}) * ${decimal} )`;
      } catch {
        return "auto";
      }
    },
    setDisplayBenchmarks() {
      this.displayedBenchmarks = Object.entries(this.metricData.benchmarks)
        .map(([label, value]) => ({
          label,
          value,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  mounted() {
    this.setDisplayBenchmarks();
    this.updateBarChart();
  },
};
</script>

<style scoped>
.single-axis-benchmark {
  display: flex;
  gap: 0.5vh;
  padding: 0.5vh;
  border-radius: 0.5vh;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0);
  transition: border 0.5s ease;
}
.single-axis-benchmark.selected {
  border: 1px solid var(--border-color);
}

.single-axis-benchmark.selected .bar {
  background: var(--border-color);
}
.single-axis-benchmark .header {
  text-align: right;
  overflow: hidden;
}
.single-axis-benchmark .bar {
  border-radius: 5px;
  background-color: var(--disabled);
  transition: all 0.5s ease;
}

.single-axis-benchmark .header,
.single-axis-benchmark .value {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  transition: all 0.125s ease;
  white-space: nowrap;
}
.single-axis-benchmark .value {
  text-align: left;
  overflow: visible;
}
#benchmark-container {
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 1vh;
  height: 42.95vh;
  justify-content: flex-start;
  align-items: stretch;
  transition: height 0.5s ease;
}
.metric-row-enter-from,
.metric-row-leave-to {
  opacity: 0;
}

.metric-row-move,
.metric-row-enter-active,
.metric-row-leave-active {
  transition: all 0.5s ease;
}

.metric-row-leave-active {
  position: relative;
}
.measure-row {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
}
</style>
